import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Dialog, Select } from 'vnetwork-ui'
import DownLoadIcons from '@/icons/DownLoadIcons'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { exportWorkdays } from '@/modules/WorkDay/services'

// Giới hạn số tháng mỗi lần export (khớp maxExportMonths ở BE).
const MAX_MONTHS = 12

/**
 * Nút mở dialog export. Đặt trong menu settings của bảng.
 * Chỉ đóng vai trò trigger — Dialog thật (ExportWorkDayDialog) render ở cấp
 * trang để không bị unmount khi Sheet settings đóng lại.
 */
export const ExportWorkDayTrigger: React.FC<{ onOpen: () => void }> = ({
    onOpen,
}) => {
    const { t } = useTranslation()
    return (
        <Button
            size="small"
            variant="contained"
            startIcon={<DownLoadIcons />}
            onClick={onOpen}
        >
            {t('download')}
        </Button>
    )
}

interface ExportWorkDayDialogProps {
    open: boolean
    onClose: () => void
}

const ExportWorkDayDialog: React.FC<ExportWorkDayDialogProps> = ({
    open,
    onClose,
}) => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(false)

    // Mặc định = tháng đang lọc trên URL (nếu có), ngược lại tháng hiện tại.
    const getDefault = () => {
        try {
            const raw = searchParams.get('filters')
            if (raw) {
                const f = JSON.parse(atob(raw))
                const y = Number(f.year)
                const m = Number(f.month)
                if (y && m) return { year: y, month: m }
            }
        } catch {
            // filters lỗi format -> dùng tháng hiện tại
        }
        const now = dayjs()
        return { year: now.year(), month: now.month() + 1 }
    }

    const def = getDefault()
    // Không hỗ trợ xuyên năm: 1 năm chung, chọn tháng bắt đầu -> kết thúc.
    const [year, setYear] = useState<number>(def.year)
    const [fromMonth, setFromMonth] = useState<number>(def.month)
    const [toMonth, setToMonth] = useState<number>(def.month)

    const currentYear = dayjs().year()
    const yearOptions = Array.from(
        { length: 6 },
        (_, i) => currentYear - i
    ).map((y) => ({ label: String(y), value: y }))
    const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1).map(
        (m) => ({
            label: `${t('month', { defaultValue: 'Tháng' })} ${m}`,
            value: m,
        })
    )

    // Cùng năm nên chỉ cần so sánh tháng.
    const monthsCount = toMonth - fromMonth + 1
    const rangeInvalid = toMonth < fromMonth
    const rangeTooLong = monthsCount > MAX_MONTHS

    const handleExport = async () => {
        if (rangeInvalid || rangeTooLong) return
        setLoading(true)
        try {
            const filters = searchParams.get('filters') || ''

            const blob = await exportWorkdays({
                page: 1,
                page_size: 99999,
                q: '',
                filters,
                from_year: year,
                from_month: fromMonth,
                to_year: year,
                to_month: toMonth,
            })

            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `workdays_${new Date().toISOString()}.xlsx`
            )
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            toast.success(t('dowload-success'))
            onClose()
        } catch (error) {
            console.error('Lỗi khi tải file Excel:', error)
            toast.error(t('download-failed'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('export-workday-title', {
                defaultValue: 'Export ngày công',
            })}
            maxWidth="sm"
            actions={
                <div className="flex gap-2 justify-end">
                    <Button variant="outlined" onClick={onClose}>
                        {t('cancel', { defaultValue: 'Hủy' })}
                    </Button>
                    <Button
                        onClick={handleExport}
                        loading={loading}
                        disabled={rangeInvalid || rangeTooLong}
                        startIcon={<DownLoadIcons />}
                    >
                        {t('download')}
                    </Button>
                </div>
            }
        >
            <div className="py-2 space-y-4">
                <div className="text-sm text-gray-500">
                    {t('export-workday-desc', {
                        defaultValue:
                            'Chọn năm và khoảng tháng để export (tối đa 12 tháng trong cùng năm). Dữ liệu gộp trên 1 sheet, có cột Năm/Tháng.',
                    })}
                </div>

                <div>
                    <div className="text-xs text-gray-500 mb-1">
                        {t('year', { defaultValue: 'Năm' })}
                    </div>
                    <Select
                        value={year}
                        onChange={(v) => setYear(Number(v))}
                        options={yearOptions}
                        size="small"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-xs text-gray-500 mb-1">
                            {t('from', { defaultValue: 'Từ tháng' })}
                        </div>
                        <Select
                            value={fromMonth}
                            onChange={(v) => setFromMonth(Number(v))}
                            options={monthOptions}
                            size="small"
                        />
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">
                            {t('to', { defaultValue: 'Đến tháng' })}
                        </div>
                        <Select
                            value={toMonth}
                            onChange={(v) => setToMonth(Number(v))}
                            options={monthOptions}
                            size="small"
                        />
                    </div>
                </div>

                {rangeInvalid && (
                    <p className="text-error text-xs">
                        {t('export-range-invalid', {
                            defaultValue: '"Đến" phải sau hoặc bằng "Từ".',
                        })}
                    </p>
                )}
                {!rangeInvalid && rangeTooLong && (
                    <p className="text-error text-xs">
                        {t('export-range-too-long', {
                            defaultValue: 'Tối đa 12 tháng mỗi lần export.',
                        })}
                    </p>
                )}
                {!rangeInvalid && !rangeTooLong && (
                    <p className="text-xs text-gray-400">
                        {t('export-range-count', {
                            defaultValue: `Sẽ export ${monthsCount} tháng`,
                            count: monthsCount,
                        })}
                    </p>
                )}
            </div>
        </Dialog>
    )
}

export default ExportWorkDayDialog
